import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../api.js';
import Button from '../ui/Button.jsx';
import Input from '../ui/Input.jsx';
import Select from '../ui/Select.jsx';
import DurationHoursMinutesInput from './DurationHoursMinutesInput.jsx';
import OptionGrid from './OptionGrid.jsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card.jsx';
import {
  buildProfilePayload,
  findInvalidProfileField,
  getInitialProfileForm,
  getMissingProfileFields,
  HOUSING_OPTIONS,
  INCOME_OPTIONS,
  isProfileComplete
} from '../utils/profileForm.js';

const YES_NO_OPTIONS = [
  { value: true, label: 'Oui' },
  { value: false, label: 'Non' }
];

export { isProfileComplete };

export const PROFILE_FORM_SECTIONS = {
  household: 'household',
  personal: 'personal',
  statistics: 'statistics'
};

export function NumberSelector({ min, max, value, onChange }) {
  const options = [];
  for (let i = min; i <= max; i++) {
    options.push(i);
  }

  return (
    <div className="flex flex-wrap gap-2">
        {options.map(num => (
            <button
                key={num}
                type="button"
                onClick={() => onChange(num)}
                className={`
                    w-10 h-10 rounded-2xl border flex items-center justify-center text-sm font-medium transition-colors
                    ${value === num
                        ? 'bg-ptcm-primary text-white border-ptcm-primary'
                        : 'bg-white dark:bg-gray-800 text-ptcm-ink dark:text-gray-200 border-ptcm-border dark:border-gray-700 hover:border-ptcm-primary/40'
                    }
                `}
            >
                {num}
            </button>
        ))}
    </div>
  );
}

function SectionBlock({ title, description, children }) {
  return (
    <section className="rounded-[28px] border border-ptcm-border dark:border-gray-800 bg-ptcm-panel/60 dark:bg-gray-950/30 p-4 sm:p-5 space-y-4">
      <div className="space-y-1">
        <h3 className="text-base font-semibold text-ptcm-ink dark:text-gray-100">{title}</h3>
        {description ? (
          <p className="text-sm text-ptcm-muted dark:text-gray-300">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

export function HouseholdContextFields({ form, setField }) {
  const hasSharedCustody = Number(form.children_shared_custody_count || 0) > 0;

  return (
    <div className="space-y-5">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-3">
        <label className="text-sm font-semibold text-ptcm-ink dark:text-gray-100">
          Dans votre foyer, combien de personnes vivent au total ? *
        </label>
        <NumberSelector min={2} max={8} value={form.household_size} onChange={(value) => setField('household_size', value)} />
      </div>

      <div className="space-y-3">
        <label className="text-sm font-semibold text-ptcm-ink dark:text-gray-100">
          Combien d'enfants vivent à 100% dans votre foyer ? *
        </label>
        <NumberSelector
          min={0}
          max={6}
          value={form.children_full_time_count}
          onChange={(value) => setField('children_full_time_count', value)}
        />
      </div>

      <div className="space-y-3">
        <label className="text-sm font-semibold text-ptcm-ink dark:text-gray-100">
          Combien d'enfants sont en garde partagée ? *
        </label>
        <NumberSelector
          min={0}
          max={6}
          value={form.children_shared_custody_count}
          onChange={(value) => {
            setField('children_shared_custody_count', value);
            if (value === 0) {
              setField('shared_custody_home_percent', 0);
            }
            if (value > 0 && (form.shared_custody_home_percent === '' || form.shared_custody_home_percent === 0)) {
              setField('shared_custody_home_percent', '');
            }
          }}
        />
      </div>

      <div className="space-y-3">
        <label className="text-sm font-semibold text-ptcm-ink dark:text-gray-100">
          Si garde partagée, quel pourcentage du temps les enfants sont chez vous ? {hasSharedCustody ? '*' : ''}
        </label>
        {hasSharedCustody ? (
          <div className="flex items-center gap-4 px-1">
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={form.shared_custody_home_percent === '' ? 50 : form.shared_custody_home_percent}
              onChange={(e) => setField('shared_custody_home_percent', parseInt(e.target.value, 10))}
              className="w-full h-2 bg-ptcm-panel rounded-lg appearance-none cursor-pointer dark:bg-gray-700 accent-[rgb(var(--ptcm-primary))]"
            />
            <span className="text-sm font-semibold w-12 text-right text-ptcm-ink dark:text-gray-100">
              {form.shared_custody_home_percent === '' ? '50%' : `${form.shared_custody_home_percent}%`}
            </span>
          </div>
        ) : (
          <div className="h-12 rounded-2xl border border-dashed border-ptcm-border dark:border-gray-700 px-4 flex items-center text-sm text-ptcm-muted dark:text-gray-400">
            Pas de garde partagée indiquée pour le moment.
          </div>
        )}
      </div>
    </div>

    <div className="space-y-4">
      <OptionGrid
        label="Votre logement *"
        value={form.housing_type}
        options={HOUSING_OPTIONS}
        columns={3}
        onChange={(value) => setField('housing_type', value)}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <OptionGrid
          label="Avez-vous des animaux de compagnie ? *"
          value={form.has_pets}
          options={YES_NO_OPTIONS}
          onChange={(value) => setField('has_pets', value)}
        />
        <OptionGrid
          label="Avez-vous des animaux d'élevage / de basse-cour ? *"
          value={form.has_livestock}
          options={YES_NO_OPTIONS}
          onChange={(value) => setField('has_livestock', value)}
        />
        <OptionGrid
          label="Possédez-vous un véhicule ? *"
          value={form.has_vehicle}
          options={YES_NO_OPTIONS}
          onChange={(value) => setField('has_vehicle', value)}
        />
        <OptionGrid
          label="Aidez-vous un proche dépendant ? *"
          value={form.cares_for_dependent}
          options={YES_NO_OPTIONS}
          onChange={(value) => setField('cares_for_dependent', value)}
        />
      </div>
    </div>
    </div>
  );
}

export function PersonalContextFields({ form, setField }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-2">
        <label className="text-sm font-semibold text-ptcm-ink dark:text-gray-100">
          Pour vous personnellement, combien d'heures travaillez-vous par semaine en moyenne ? *
        </label>
        <DurationHoursMinutesInput
          value={form.work_hours_per_week}
          onChange={(value) => setField('work_hours_per_week', value)}
          maxHours={90}
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-ptcm-ink dark:text-gray-100">
          Pour vous, combien d'heures dormez-vous par nuit en moyenne ? *
        </label>
        <DurationHoursMinutesInput
          value={form.sleep_hours_per_night}
          onChange={(value) => setField('sleep_hours_per_night', value)}
          maxHours={14}
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-ptcm-ink dark:text-gray-100">
          Pour vous, combien d'heures de transport avez-vous par semaine en moyenne ? *
        </label>
        <DurationHoursMinutesInput
          value={form.commute_hours_per_week}
          onChange={(value) => setField('commute_hours_per_week', value)}
          maxHours={30}
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-ptcm-ink dark:text-gray-100">
          Sur une année, combien de semaines êtes-vous absent·e du foyer ? *
        </label>
        <Input
          required
          type="number"
          min={0}
          max={52}
          value={form.away_weeks_per_year}
          onChange={(e) => setField('away_weeks_per_year', e.target.value)}
        />
      </div>
    </div>
  );
}

export function OptionalProfileFields({ form, setField }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-2">
        <label className="text-sm font-semibold text-ptcm-ink dark:text-gray-100">
          Département du foyer
        </label>
        <Input
          type="text"
          maxLength={5}
          placeholder="75"
          value={form.department}
          onChange={(e) => setField('department', e.target.value)}
        />
        <div className="text-xs text-ptcm-muted dark:text-gray-400">Facultatif.</div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-ptcm-ink dark:text-gray-100">
          Année de naissance
        </label>
        <Input
          type="number"
          min={1900}
          max={new Date().getFullYear()}
          placeholder="1990"
          value={form.birth_year}
          onChange={(e) => setField('birth_year', e.target.value)}
        />
        <div className="text-xs text-ptcm-muted dark:text-gray-400">Facultatif.</div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-ptcm-ink dark:text-gray-100">Genre</label>
        <Select value={form.gender} onChange={(e) => setField('gender', e.target.value)}>
          <option value="">Sélectionner...</option>
          <option value="male">Homme</option>
          <option value="female">Femme</option>
          <option value="other">Autre</option>
        </Select>
        <div className="text-xs text-ptcm-muted dark:text-gray-400">Facultatif.</div>
      </div>

      <div className="space-y-2 sm:col-span-2">
        <label className="text-sm font-semibold text-ptcm-ink dark:text-gray-100">
          Revenu annuel net personnel (avant impôt)
        </label>
        <Select value={form.annual_income_bracket} onChange={(e) => setField('annual_income_bracket', e.target.value)}>
          <option value="">Sélectionner une option...</option>
          {INCOME_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
        <div className="text-xs text-ptcm-muted dark:text-gray-400">
          Facultatif. Utilisé uniquement pour des statistiques agrégées.
        </div>
      </div>
    </div>
  );
}

export default function HouseholdProfileForm({
  token,
  member,
  onSaved,
  embedded = false,
  visibleSections = [
    PROFILE_FORM_SECTIONS.household,
    PROFILE_FORM_SECTIONS.personal,
    PROFILE_FORM_SECTIONS.statistics
  ],
  submitLabel = 'Enregistrer les modifications',
  headerTitle = 'Informations personnelles et foyer',
  headerDescription = 'Les questions sont regroupées pour distinguer clairement ce qui relève de vous et ce qui relève du foyer.'
}) {
  const initial = useMemo(() => getInitialProfileForm(member), [member]);

  const [form, setForm] = useState(initial);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setForm(initial);
  }, [initial]);

  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaved(false);

    const missing = getMissingProfileFields(form);
    if (missing.length > 0) {
      setError('Veuillez compléter les champs obligatoires avant de continuer.');
      return;
    }

    const payload = buildProfilePayload(form);

    const invalidField = findInvalidProfileField(form, payload);
    if (invalidField) {
      setError('Certaines valeurs ne respectent pas les limites demandées.');
      return;
    }

    setIsSaving(true);
    try {
      await apiFetch('/api/family/profile', { token, method: 'POST', body: payload });
      setSaved(true);
      onSaved?.();
      setTimeout(() => setSaved(false), 1200);
    } catch (err) {
      setError(err?.message || 'Impossible d’enregistrer.');
    } finally {
      setIsSaving(false);
    }
  };

  const content = (
    <>
      {error ? (
        <div className="mb-4 rounded-[24px] border border-red-200/70 bg-red-50/90 p-4 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="space-y-4 text-left relative flex flex-col h-full">
        <div className="space-y-5 pb-24 sm:pb-4">
          {visibleSections.includes(PROFILE_FORM_SECTIONS.household) ? (
            <SectionBlock
              title="Votre foyer"
              description="Ces réponses décrivent la composition du foyer dans lequel vous vivez."
            >
              <HouseholdContextFields form={form} setField={setField} />
            </SectionBlock>
          ) : null}

          {visibleSections.includes(PROFILE_FORM_SECTIONS.personal) ? (
            <SectionBlock
              title="Vous"
              description="Ces réponses décrivent votre rythme de vie personnel pour mieux contextualiser les résultats."
            >
              <PersonalContextFields form={form} setField={setField} />
            </SectionBlock>
          ) : null}

          {visibleSections.includes(PROFILE_FORM_SECTIONS.statistics) ? (
            <SectionBlock
              title="Infos statistiques facultatives"
              description="Vous pourrez les laisser vides et les compléter plus tard si vous le souhaitez."
            >
              <OptionalProfileFields form={form} setField={setField} />
            </SectionBlock>
          ) : null}
        </div>

        <div className={embedded
          ? "fixed bottom-0 left-0 right-0 p-4 bg-ptcm-bg/95 dark:bg-gray-900 border-t border-ptcm-border dark:border-gray-800 flex items-center gap-3 z-10 backdrop-blur-xl"
          : "flex items-center gap-2 pt-2"
        }>
          <Button type="submit" size="lg" disabled={isSaving} className="w-full sm:w-auto shadow-lg shadow-blue-500/20">
            {isSaving ? 'Enregistrement…' : submitLabel}
          </Button>
          {saved ? <div className="text-sm text-emerald-700 dark:text-green-200 animate-pulse font-medium">Enregistré !</div> : null}
        </div>
      </form>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{headerTitle}</CardTitle>
        <CardDescription>
          {headerDescription}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
}
